import * as T from 'three';
import type { TravelMode } from '@london-life/contracts';
import { createSceneKit } from '@london-life/game/scene/kit';

// What a journey looks like on the map: a London bus, a black cab, a DLR train or a tube train, drawn in the
// map's block style and facing +z (the way it drives). Walking is the player's own character (the map screen
// adds it). Each one is its own little scene: built when the journey starts, thrown away when it ends.

export function buildVehicle(mode: Exclude<TravelMode, 'walk'>) {
  const kit = createSceneKit();
  const group = new T.Group();
  const wheels = (spots: [number, number][], r: number) => {
    for (const [x, z] of spots) kit.cylinder(r, 0.22, '#262b31', x, r, z, group, r, 10).rotation.z = Math.PI / 2;
  };
  switch (mode) {
    case 'bus': {
      // a red double-decker: two decks of windows, the destination blind, the route number
      kit.box(1.9, 2.4, 5.4, '#cf3a2f', 0, 1.45, 0, group);
      kit.box(1.92, 0.12, 5.42, '#8c2a22', 0, 2.65, 0, group);
      for (const level of [1.2, 2.2])
        for (const side of [-1, 1]) kit.box(0.05, 0.5, 4.6, '#2d4253', side * 0.97, level, -0.1, group);
      kit.box(1.5, 0.5, 0.05, '#2d4253', 0, 2.2, 2.71, group);
      kit.lit(1.2, 0.22, 0.04, '#ffd23f', 0, 2.62, 2.71, group, 0.9);
      kit.box(1.5, 0.9, 0.05, '#2d4253', 0, 1.25, 2.71, group);
      wheels(
        [
          [-0.9, 1.7],
          [0.9, 1.7],
          [-0.9, -1.7],
          [0.9, -1.7],
        ],
        0.36,
      );
      break;
    }
    case 'cab': {
      // a black cab: the rounded body, the yellow TAXI light on the roof
      kit.box(1.4, 0.75, 2.9, '#1f242b', 0, 0.68, 0, group);
      kit.box(1.25, 0.6, 1.6, '#2b323a', 0, 1.35, -0.2, group);
      for (const side of [-1, 1]) kit.box(0.04, 0.38, 1.3, '#9fb6c6', side * 0.63, 1.38, -0.2, group);
      kit.box(1.1, 0.4, 0.04, '#9fb6c6', 0, 1.38, 0.61, group);
      kit.lit(0.45, 0.14, 0.2, '#ffcc33', 0, 1.72, -0.1, group, 1.2);
      wheels(
        [
          [-0.68, 0.95],
          [0.68, 0.95],
          [-0.68, -0.95],
          [0.68, -0.95],
        ],
        0.27,
      );
      break;
    }
    case 'dlr':
    case 'tube': {
      // three carriages: the DLR's white and blue on its viaduct, or the tube's red, white and blue
      const tube = mode === 'tube';
      const body = tube ? '#e9ecef' : '#e8ecef';
      const band = tube ? '#c81e1e' : '#1c7fc0';
      for (const dz of [-5.1, 0, 5.1]) {
        kit.box(1.7, 1.6, 4.8, body, 0, 1.0, dz, group);
        kit.box(1.72, 0.42, 4.8, band, 0, 0.42, dz, group);
        if (tube) kit.box(1.72, 0.2, 4.8, '#1d3c8f', 0, 1.72, dz, group);
        for (const side of [-1, 1]) kit.box(0.05, 0.45, 4.2, '#2d4253', side * 0.86, 1.15, dz, group);
      }
      kit.lit(1.2, 0.2, 0.05, '#fff6d6', 0, 0.9, 7.53, group, 1);
      break;
    }
  }
  group.traverse((object) => {
    object.castShadow = false;
  });
  return {
    group,
    // how high the name tag floats over it
    height: mode === 'bus' ? 3.4 : mode === 'cab' ? 2.2 : 2.2,
    dispose: () => kit.dispose(),
  };
}
